import classNames from "classnames";
import { useTranslation } from "react-i18next";
import MenuItem from "@mui/material/MenuItem";

import { useConfigContext } from "shared/contexts/ConfigContext";

import {
  DUCK_PAGE_LEFT_PANEL_WIDTH,
  MAX_CHATS_DROPDOWN_HEIGHT,
} from "pages/Duck/constants";

import Typography from "features/components/Typography";
import LoadingIcon from "features/ui/Icons/LoadingIcon";

import { useThreadList } from "./hooks";
import SelectThreadUser from "./SelectThreadUser";
import ThreadListItem from "./ThreadListItem";
import ThreadListSearch from "./ThreadListSearch";

interface ThreadListPanelProps {
  onClose?: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

const ThreadListPanel = ({ onClose, inputRef }: ThreadListPanelProps) => {
  const { t } = useTranslation("duck");
  const {
    filteredThreads,
    currentThreadId,
    selectThread,
    isDisabled,
    hasThreads,
    searchQuery,
    handleSearchChange,
    createNewThread,
    deleteThread,
    setUseListThreadArgs,
    useListThreadArgs,
    isLoadingThreads,
  } = useThreadList();

  const { userInfo } = useConfigContext();

  const handleThreadClick = (threadId: string) => {
    if (isDisabled) {
      return;
    }

    selectThread(threadId, true);
    onClose?.();
  };

  const renderEmptyState = () => {
    if (filteredThreads.length === 0 && hasThreads) {
      return (
        <MenuItem disabled={true} sx={{ px: 2, py: 1 }}>
          <Typography variant="caption">{t("no_chats_found")}</Typography>
        </MenuItem>
      );
    }

    return (
      <MenuItem disabled={true} sx={{ px: 2, py: 1 }}>
        <Typography variant="caption">{t("no_chats_yet")}</Typography>
      </MenuItem>
    );
  };

  return (
    <div
      style={{ width: `${DUCK_PAGE_LEFT_PANEL_WIDTH}px` }}
      className="flex flex-col h-full"
    >
      {userInfo.isSuperAdmin && (
        <SelectThreadUser
          setThreadListArguments={setUseListThreadArgs}
          useListThreadArgs={useListThreadArgs}
        />
      )}
      {!isLoadingThreads && (
        <ThreadListSearch
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          inputRef={inputRef}
        />
      )}
      {isLoadingThreads && (
        <div className="flex px-4 py-1 gap-x-2 items-center justify-center">
          <LoadingIcon />
          <span> {t("loading_threads")}</span>
        </div>
      )}

      {!isLoadingThreads && filteredThreads.length === 0 && renderEmptyState()}

      {!isLoadingThreads && filteredThreads.length > 0 && (
        <div
          className={classNames(
            "flex flex-col overflow-y-auto pb-2 flex-1 min-h-0 w-full"
          )}
          style={{
            maxHeight: `${MAX_CHATS_DROPDOWN_HEIGHT}px`,
          }}
          aria-label={t("chats")}
          data-testid="thread-list-container-duck"
        >
          {filteredThreads.map((thread) => (
            <ThreadListItem
              key={thread.id}
              thread={thread}
              isSelected={thread.id === currentThreadId}
              isDisabled={isDisabled}
              onClick={() => handleThreadClick(thread.id)}
              deleteThread={deleteThread}
              onDelete={(threadID: string) => {
                if (threadID === currentThreadId) {
                  createNewThread();
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ThreadListPanel;
